import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useDispatch, useSelector } from "react-redux";
import { FiPlay, FiSquare } from "react-icons/fi";
import { scoreActions } from "../../store/score";
import { practice } from "../../lib/practice/session";
import { sendToDaw } from "../../lib/daw/store";
import { usePlatform } from "../platform/react";
import { toolPage } from "../../lib/tools/registry";
import RhythmPreview from "../book-builder/RhythmPreview";
import usePreviewAudio from "../social/usePreviewAudio";
import RhythmDialog from "../social/RhythmDialog";
import styles from "./tools.module.css";

// Counts a tool's use (the stats' uses, by the ref tool:<id>), to learn which tools matter.
export function countUse(id) {
  fetch("/api/social/stats/use", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ref: `tool:${id}` }) }).catch(() => {});
}

// Every tool's panel has the same frame (docs/refactor-plan.md, Phase 2): what it's working on and
// its inputs, then its results, then where they can go. `results` are rhythms ({ key, title, score,
// tempo, detail }); `own` is true on the tool's own page, which it doesn't link to.
export default function ToolFrame({ tool, idea, children, results = null, busy = false, error = "", note = "", own = false }) {
  const router = useRouter(), dispatch = useDispatch(), preview = usePreviewAudio(), site = usePlatform();
  const [chosen, setChosen] = useState([]), [saving, setSaving] = useState(null), [sent, setSent] = useState(""), [added, setAdded] = useState("");
  // In the Score, with a bar chosen, results can become that bar's takes (src/lib/takes.js).
  const { score: scored, selectedNoteIndex } = useSelector((state) => state.score.present);
  const barIndex = router.pathname === "/" ? selectedNoteIndex?.measureIndex : undefined, bar = barIndex != null ? scored.measures[barIndex] : null;
  useEffect(() => { setChosen(results?.length ? [results[0].key] : []); setSent(""); setAdded(""); }, [results]);
  const picked = (results || []).filter((result) => chosen.includes(result.key));
  const toggle = (key) => setChosen((keys) => keys.includes(key) ? keys.filter((value) => value !== key) : [...keys, key]);
  const open = (rhythm) => { dispatch(scoreActions.updateScore({ score: rhythm.score, name: rhythm.title, tempo: rhythm.tempo || 90, mutations: [] })); return router.push("/"); };
  // Each chosen result's bar in the same place (or its only bar), in the bar's time signature, as
  // another take of it, with how alike it is.
  async function asTakes() {
    const { similarity } = await import("../../lib/similarity/similarity"), one = (measure) => ({ ...scored, measures: [measure] });
    const fits = picked.map((rhythm) => rhythm.score.measures[rhythm.score.measures.length > barIndex ? barIndex : 0]).filter((measure) => measure?.timeSig.num === bar.timeSig.num && measure.timeSig.type === bar.timeSig.type);
    if (!fits.length) { setSent(`None of these is in bar ${barIndex + 1}'s time signature.`); return; }
    const alike = (measure) => { try { return similarity(one(measure), one(bar)).score; } catch { return null; } };
    dispatch(scoreActions.addTakes({ index: barIndex, takes: fits.map((measure) => ({ measure, how: tool.how || "variation", ...(idea?.ref ? { ref: idea.ref } : {}), similarity: alike(measure) })) }));
    setSent(""); setAdded(`${fits.length === 1 ? "A take" : `${fits.length} takes`} added to bar ${barIndex + 1}.`);
  }
  async function toSong() {
    try {
      let last = null;
      for (const rhythm of picked) last = await sendToDaw({ title: rhythm.title, score: rhythm.score, tempo: rhythm.tempo || 90, from: tool.name });
      if (last) router.push(`/daw?add=${last.id}`);
    } catch (failure) { setSent(`The DAW couldn't take it: ${failure.message}`); }
  }
  return <section className={styles.frame} aria-label={tool.name}>
    <div className={styles.head}>
      <div><h3>{tool.name}</h3><p>{tool.does}{tool.cost === "studio" ? " · uses studio minutes" : ""}</p></div>
      {!own && <Link href={toolPage(tool)}>Open on its own</Link>}
    </div>
    <p className={styles.working}>{idea ? <>Working on <span className={styles.chip}>{idea.title}</span></> : "Working on nothing open yet."}</p>
    {children}
    {busy && <p className={styles.status} role="status">Working…</p>}
    {error && <p className={styles.error} role="alert">{error}</p>}
    {note && <p className={styles.status} role="status">{note}</p>}
    {results?.length > 0 && <>
      <ul className={styles.results} aria-label={`${tool.name}'s results`}>
        {results.map((result) => <li key={result.key} className={styles.result} data-chosen={chosen.includes(result.key) ? "true" : "false"}>
          <div className={styles.resultHead}>
            <button type="button" className={styles.play} aria-label={preview.playing === result.key ? `Stop ${result.title}` : `Play ${result.title}`}
              onClick={() => preview.playing === result.key ? preview.stop() : preview.play(result.key, result.score, result.tempo || 90)}>
              {preview.playing === result.key ? <FiSquare aria-hidden="true" /> : <FiPlay aria-hidden="true" />}</button>
            <strong>{result.title}</strong>
            <label><input type="checkbox" checked={chosen.includes(result.key)} onChange={() => toggle(result.key)} /> Choose</label>
          </div>
          <div className={styles.notation} role="img" aria-label={`${result.title}, notation`}><RhythmPreview score={result.score} minWidth={220} fit compact maxHeight="86px" /></div>
          {result.detail && <p className={styles.detail}>{result.detail}</p>}
        </li>)}
      </ul>
      <div className={styles.sendTo} role="group" aria-label="Send the chosen ideas to">
        <span>Send {picked.length === 1 ? "it" : `the ${picked.length}`} to</span>
        <button type="button" className={styles.button} disabled={!picked.length} onClick={() => open(picked[0])}>Score</button>
        {site.appEnabled("daw") && <button type="button" className={styles.button} disabled={!picked.length} onClick={toSong}>Song</button>}
        <button type="button" className={styles.button} disabled={!picked.length} onClick={() => { practice.start(); open(picked[0]); }}>Practice</button>
        <button type="button" className={styles.button} disabled={picked.length !== 1} onClick={() => setSaving(picked[0])}>Save</button>
        {bar && <button type="button" className={styles.button} disabled={!picked.length} onClick={asTakes}>{picked.length > 1 ? `${picked.length} takes` : "A take"} on bar {barIndex + 1}</button>}
      </div>
      {sent && <p className={styles.error} role="alert">{sent}</p>}
      {added && <p className={styles.status} role="status">{added}</p>}
    </>}
    {saving && <RhythmDialog composition={{ score: saving.score, name: saving.title, tempo: saving.tempo || 90, mutations: [],
      // Saved, it joins the family of the idea the tool worked on.
      ...(idea?.ref && tool.how ? { from: { refs: [idea.ref], how: tool.how } } : {}) }} onClose={() => setSaving(null)} onSaved={() => setSaving(null)} />}
  </section>;
}
